import React, { useState } from "react";
import { useCustomerStore } from "../../../../Store/Customer";
import ViewMoreIcon from "../../../../Assets/ActionIcons/ViewMoreIcon.svg";
import ViewLessIcon from "../../../../Assets/ActionIcons/ViewLessIcon.svg";
import { ReactComponent as EditIcon } from "../../../../Assets/ActionIcons/EditIcon.svg";
import { ReactComponent as DeleteIcon } from "../../../../Assets/ActionIcons/DeleteIcon.svg";
import { ReactComponent as PlusIcon } from "../../../../Assets/ActionIcons/PlusIcon.svg";
import { useAuth0 } from "@auth0/auth0-react";
import { Contact } from "../../../../Contracts/Customer/Contact";
import { validateEmail } from "../../../../Contracts/Common";

const CustomerEmailRecipientsCard: React.FC = () => {
  const { isLoading, customerDetailEntity, editContact, createContact, deleteContact } =
    useCustomerStore();
  const { getAccessTokenSilently } = useAuth0();
  const [viewMore, setViewMore] = useState(false);
  const [contactBeingEdited, setContactBeingEdited] = useState<{
    id: number;
    isLoading: boolean;
    name: string;
    email: string[];
    validName: boolean;
    validEmail: boolean;
  }>({
    id: -1,
    isLoading: false,
    name: "",
    email: [""],
    validEmail: true,
    validName: true
  });
  const [contactBeingCreated, setContactBeingCreated] = useState<{
    creating: boolean;
    isLoading: boolean;
    name: string;
    email: string[];
    validName: boolean;
    validEmail: boolean;
  }>({
    creating: false,
    isLoading: false,
    name: "",
    email: [""],
    validEmail: true,
    validName: true
  });

  const contactsToShow = viewMore
    ? customerDetailEntity?.contacts?.data || []
    : (customerDetailEntity?.contacts?.data || []).slice(0, 2);

  const onContactEdit = (contact: Contact) => async () => {
    if (!validateFields()) {
      return;
    }
    setContactBeingEdited({
      ...contactBeingEdited,
      isLoading: true
    });
    await editContact(
      getAccessTokenSilently,
      contact.id,
      contactBeingEdited.name,
      contactBeingEdited.email
    );
    setContactBeingEdited({
      id: -1,
      isLoading: false,
      name: "",
      email: [""],
      validEmail: true,
      validName: true
    });
  };

  const onContactCreate = async () => {
    if (!validateFields()) {
      return;
    }
    if (contactBeingCreated.name && contactBeingCreated.email && customerDetailEntity?.id) {
      setContactBeingCreated({
        ...contactBeingCreated,
        isLoading: true
      });
      await createContact(
        getAccessTokenSilently,
        customerDetailEntity.id,
        contactBeingCreated.name,
        contactBeingCreated.email
      );
    }
    setContactBeingCreated({
      creating: false,
      isLoading: false,
      name: "",
      email: [""],
      validEmail: true,
      validName: true
    });
  };

  const onContactDelete = (contact: Contact) => async () => {
    const confirmDelete = window.confirm("Are you sure you want to delete this item?");
    if (confirmDelete) {
      await deleteContact(getAccessTokenSilently, contact.id);
    }
  };

  const validateFields = (): boolean => {
    if (contactBeingCreated.creating) {
      const validName = !!contactBeingCreated.name && contactBeingCreated.name.length > 0;
      const validEmail = contactBeingCreated.email.every(email => validateEmail(email));
      setContactBeingCreated({
        ...contactBeingCreated,
        validName,
        validEmail
      });
      return validName && validEmail;
    }

    if (contactBeingEdited.id !== -1) {
      const validName = !!contactBeingEdited.name && contactBeingEdited.name.length > 0;
      const validEmail = contactBeingEdited.email.every(email => validateEmail(email));
      setContactBeingEdited({
        ...contactBeingEdited,
        validName,
        validEmail
      });
      return validName && validEmail;
    }
    return true;
  };

  return (
    <div className="rounded p-3 border border-gray-300 my-2">
      <div className="flex items-center justify-between mb-4">
        <span className="font-semibold">Email Recipients</span>
        <button
          onClick={() =>
            setContactBeingCreated({
              ...contactBeingCreated,
              creating: true
            })
          }
          className="text-blue-600 hover:text-blue-700 hover:scale-105 transition-transform flex items-center"
        >
          <PlusIcon className="h-4 w-4 mx-2" />
          <span>New Contact</span>
        </button>
      </div>
      {contactBeingCreated.creating && (
        <div className="flex justify-between my-2 items-start">
          <div className="flex flex-col">
            <label className="my-1">
              Name<sup className="text-red-600">*</sup>
            </label>
            <input
              type="text"
              value={contactBeingCreated.name}
              onChange={e =>
                setContactBeingCreated({
                  ...contactBeingCreated,
                  name: e.target.value
                })
              }
              className="p-1 my-1 border border-gray-300 rounded"
            />
            {!contactBeingCreated.validName && (
              <div className="italic text-red-600">Please enter a valid name</div>
            )}
            <label className="my-1">
              Email<sup className="text-red-600">*</sup>
            </label>
            <input
              type="email"
              value={contactBeingCreated.email}
              onChange={e =>
                setContactBeingCreated({
                  ...contactBeingCreated,
                  email: [e.target.value]
                })
              }
              className="p-1 my-1 border border-gray-300 rounded"
            />
            {!contactBeingCreated.validEmail && (
              <div className="italic text-red-600">Please enter a valid email</div>
            )}
          </div>
          <div className="flex items-center justify-center">
            <button
              onClick={() =>
                setContactBeingCreated({
                  creating: false,
                  isLoading: false,
                  name: "",
                  email: [""],
                  validEmail: true,
                  validName: true
                })
              }
              className={`text-red-600 mr-4 hover:text-blue-600 hover:scale-110 transition-transform ${
                contactBeingCreated.isLoading && "cursor-progress"
              }`}
              disabled={contactBeingCreated.isLoading}
            >
              Discard
            </button>
            <button
              onClick={onContactCreate}
              className={`hover:text-blue-500 hover:scale-110 transition-transform ${
                contactBeingCreated.isLoading && "cursor-progress"
              }`}
              disabled={contactBeingCreated.isLoading}
            >
              Save
            </button>
          </div>
        </div>
      )}
      {contactsToShow.map(contact => (
        <div
          className="flex items-start justify-between p-2 border-t border-gray-300"
          key={contact.id}
        >
          <div>
            {contactBeingEdited.id === contact.id ? (
              <div className="flex flex-col">
                <label className="my-1">
                  Name<sup className="text-red-600">*</sup>
                </label>
                <input
                  type="text"
                  value={contactBeingEdited.name}
                  onChange={e =>
                    setContactBeingEdited({
                      ...contactBeingEdited,
                      name: e.target.value
                    })
                  }
                  className="p-1 my-1 border border-gray-300 rounded"
                />
                {!contactBeingEdited.validName && (
                  <div className="italic text-red-600">Please enter a valid name</div>
                )}
                <label className="my-1">
                  Email<sup className="text-red-600">*</sup>
                </label>
                <input
                  type="email"
                  value={contactBeingEdited.email}
                  onChange={e =>
                    setContactBeingEdited({
                      ...contactBeingEdited,
                      email: [e.target.value]
                    })
                  }
                  className="p-1 my-1 border border-gray-300 rounded"
                />
                {!contactBeingEdited.validEmail && (
                  <div className="italic text-red-600">Please enter a valid email</div>
                )}
              </div>
            ) : (
              <>
                <div>{contact.name || "Undefined"}</div>
                <div className="text-gray-500 break-all whitespace-pre-wrap">
                  {contact.email.map((email: string, index: number) => (
                    <div key={index}>
                      {email}
                      {index < contact.email.length - 1 && ", "}
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
          {!contact.from_integration && (
            <div className="flex items-center">
              {contactBeingEdited.id === contact.id ? (
                <>
                  <button
                    onClick={() =>
                      setContactBeingEdited({
                        id: -1,
                        isLoading: false,
                        name: "",
                        email: [""],
                        validEmail: true,
                        validName: true
                      })
                    }
                    className={`text-red-600 mr-4 hover:text-red-600 hover:scale-110 transition-transform ${
                      contactBeingEdited.isLoading && "cursor-progress"
                    }`}
                    disabled={contactBeingEdited.isLoading}
                  >
                    Discard
                  </button>
                  <button
                    onClick={onContactEdit(contact)}
                    className={`hover:text-blue-500 hover:scale-110 transition-transform ${
                      contactBeingEdited.isLoading && "cursor-progress"
                    }`}
                    disabled={contactBeingEdited.isLoading}
                  >
                    Save
                  </button>
                </>
              ) : (
                <>
                  <EditIcon
                    onClick={() =>
                      setContactBeingEdited({
                        id: contact.id,
                        isLoading: false,
                        name: contact.name,
                        email: contact.email,
                        validEmail: true,
                        validName: true
                      })
                    }
                    className="mr-4 cursor-pointer hover:text-blue-600 hover:scale-125 hover:shadow-lg transition-transform"
                  />
                  <DeleteIcon
                    onClick={onContactDelete(contact)}
                    className="cursor-pointer hover:text-blue-600 hover:scale-125 hover:shadow-lg transition-transform"
                  />
                </>
              )}
            </div>
          )}
        </div>
      ))}
      {(customerDetailEntity?.contacts?.data || []).length > 2 && (
        <div className="flex justify-center border-t border-gray-300 py-2">
          <button onClick={() => setViewMore(!viewMore)}>
            {viewMore ? (
              <span className="flex items-center text-blue-600">
                <img src={ViewLessIcon} alt="View less" className="mr-2" />
                View less
              </span>
            ) : (
              <span className="flex items-center text-blue-600">
                <img src={ViewMoreIcon} alt="View more" className="mr-2" />
                View more
              </span>
            )}
          </button>
        </div>
      )}
      {isLoading &&
        [1, 2].map(index => (
          <div key={index} className="p-2 border-t border-gray-300 flex items-center animate-pulse">
            <div className="bg-slate-200 h-6 w-6 rounded-full mr-4" />
            <div className="bg-slate-200 h-6 w-20 rounded-lg" />
          </div>
        ))}
    </div>
  );
};

export default CustomerEmailRecipientsCard;
